<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">library_books</i>
      <h2>{{ $t('dash.reporting.section.posts.title') }}</h2>
      <div class="filter-wrap" v-click-outside="closeFilter">
        <button
          class="filter-toggle-btn"
          :class="{ 'filter-toggle-btn--active': hasActiveFilters }"
          @click="showFilter = !showFilter"
        >
          <i class="material-icons">filter_list</i>
          <span v-if="activeFilterCount" class="filter-badge">{{ activeFilterCount }}</span>
        </button>
        <ReportingFilter
          v-if="showFilter"
          :filter="filter"
          :creators="creatorsAvailable"
          :labels="labelsAvailable"
          @update:filter="filter = $event"
        />
      </div>
    </div>

    <!-- Recherche dans les publications -->
    <div class="posts-search" v-if="posts.length">
      <i class="material-icons posts-search__icon">search</i>
      <input
        v-model="search"
        class="posts-search__input"
        type="text"
        :placeholder="$t('dash.reporting.posts_search_placeholder')"
      />
      <button v-if="search" class="posts-search__clear" @click="search = ''">
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="post-empty" v-if="!filteredSortedPosts.length">
      <i class="material-icons">inbox</i>
      <p>{{ search ? $t('dash.reporting.no_posts_search') : $t('dash.reporting.no_posts') }}</p>
    </div>

    <template v-else>
      <div class="post-table">
        <div class="post-table__head" :style="{ gridTemplateColumns: gridTemplate }">
          <span class="col-header sortable" :class="sortClass('date')" @click="onSort('date')">
            {{ cap($t('dash.reporting.pdf.listItem.date')) }}<i class="material-icons sort-icon">{{ sortIcon('date') }}</i>
          </span>
          <span class="col-header">{{ cap($t('dash.reporting.pdf.listItem.publication')) }}</span>
          <span class="col-header sortable align-right" :class="sortClass('impressions')" @click="onSort('impressions')">
            {{ cap($t('dash.reporting.pdf.listItem.print')) }}<i class="material-icons sort-icon">{{ sortIcon('impressions') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('reactions')" @click="onSort('reactions')">
            {{ cap($t('dash.reporting.pdf.listItem.reactions')) }}<i class="material-icons sort-icon">{{ sortIcon('reactions') }}</i>
          </span>
          <span class="col-header sortable align-right" :class="sortClass('comments')" @click="onSort('comments')">
            {{ cap($t('dash.reporting.pdf.listItem.comments')) }}<i class="material-icons sort-icon">{{ sortIcon('comments') }}</i>
          </span>
          <span v-if="showShares" class="col-header sortable align-right" :class="sortClass('shares')" @click="onSort('shares')">
            {{ cap($t('dash.reporting.pdf.listItem.shares')) }}<i class="material-icons sort-icon">{{ sortIcon('shares') }}</i>
          </span>
          <Tooltip v-if="showClicks" :text="$t('dash.reporting.tooltip.clicks_total')" position="bottom">
            <span class="col-header sortable align-right" :class="sortClass('clicks')" @click="onSort('clicks')">
              {{ cap($t('dash.reporting.clic')) }}<i class="material-icons sort-icon">{{ sortIcon('clicks') }}</i>
            </span>
          </Tooltip>
          <Tooltip v-if="showShortLinks" :text="$t('dash.reporting.tooltip.short_link_clicks')" position="bottom">
            <span class="col-header sortable align-right" :class="sortClass('short_clicks')" @click="onSort('short_clicks')">
              {{ cap($t('dash.reporting.short_links.column')) }}<i class="material-icons sort-icon">{{ sortIcon('short_clicks') }}</i>
            </span>
          </Tooltip>
          <Tooltip :text="networkTooltip('dash.reporting.tooltip.post_engagement', 'dash.reporting.tooltip.engagement_rate')" position="bottom">
            <span class="col-header sortable align-right" :class="sortClass('engagement')" @click="onSort('engagement')">
              {{ cap($t('dash.reporting.pdf.listItem.engagement')) }}<i class="material-icons sort-icon">{{ sortIcon('engagement') }}</i>
            </span>
          </Tooltip>
          <span class="col-header sortable align-right score-header" :class="sortClass('score')" @click="onSort('score')">
            Score<Tooltip :text="scoreFormula" position="bottom"><span class="score-info">ⓘ</span></Tooltip><i class="material-icons sort-icon">{{ sortIcon('score') }}</i>
          </span>
          <span class="col-header"></span>
        </div>
        <div class="post-table__row" v-for="post in paginatedFilteredPosts" :key="post.id || post.post_id" :style="{ gridTemplateColumns: gridTemplate }">
          <span class="cell-date">{{ formatDate(post.created_time) }}</span>
          <div class="cell-post">
            <div class="post-thumb">
              <img v-if="post.attachment_url" :src="post.attachment_url" alt="">
              <i v-else class="material-icons">image</i>
            </div>
            <div class="post-text">
              <p class="post-msg">{{ post.message }}</p>
              <div class="post-meta">
                <Tooltip v-if="isSolutionPost(post)" :text="$t('dash.reporting.filter.solution_only')" position="top">
                  <span class="solution-badge"><img src="/img/icons/favicon-16x16.png" alt="Mashup Web" class="solution-badge__icon"></span>
                </Tooltip>
                <span class="type-badge" :class="typeBadgeClass(post.type)">{{ typeLabel(post.type) }}</span>
                <template v-if="newsIdFor(post)">
                  <div
                    class="post-label-chip"
                    v-for="label in resolvedLabelsFor(post)"
                    :key="label.id"
                    :style="{ backgroundColor: label.color }"
                  >
                    <i v-if="parseIcon(label.icon) && parseIcon(label.icon).type === 'fa'" class="fa-solid" :class="`fa-${parseIcon(label.icon).value}`"></i>
                    <span v-else-if="parseIcon(label.icon)" class="post-label-emoji">{{ parseIcon(label.icon).value }}</span>
                    <span>{{ label.name }}</span>
                  </div>
                  <div class="post-label-add" @click.stop="toggleLabelPicker($event, post)">
                    <i class="fa-solid fa-plus"></i>
                    <span>{{ $t('dash.posts.labels.add-tooltip') }}</span>
                  </div>
                </template>
              </div>
              <div
                v-if="labelPickerOpenFor === postKey(post)"
                class="post-label-picker-backdrop"
                @click="closeLabelPicker(post)"
              ></div>
              <LabelPicker
                v-if="labelPickerOpenFor === postKey(post)"
                class="post-label-picker-popover"
                :style="{ top: labelPickerPosition.top + 'px', left: labelPickerPosition.left + 'px' }"
                :selected="postLabelIds(post)"
                @update:selected="ids => onLabelsUpdate(post, ids)"
                @close="closeLabelPicker(post)"
              />
            </div>
          </div>
          <span class="cell-num">{{ fmtImpression(post) }}</span>
          <div class="cell-reactions" v-if="postReactionItems(post)">
            <div class="reaction-grid">
              <div class="reaction-col" v-for="item in postReactionItems(post)" :key="item.key">
                <img v-if="item.icon" :src="item.icon" :alt="item.label" class="reaction-icon">
                <span v-else class="reaction-icon reaction-unknown">{{ item.key.substring(0, 2).toUpperCase() }}</span>
                <span class="reaction-count">{{ item.count }}</span>
              </div>
            </div>
            <div class="reaction-total">{{ fmtReactions(post) }}</div>
          </div>
          <span v-else class="cell-num">{{ fmtReactions(post) }}</span>
          <span class="cell-num">{{ post.comments_count || 0 }}</span>
          <span v-if="showShares" class="cell-num">{{ post.shares_count || 0 }}</span>
          <span v-if="showClicks" class="cell-num cell-clicks">
            {{ fmtTotalClicks(post) }}
            <Tooltip v-if="post.link_clicks" :text="$t('dash.reporting.tooltip.link_clicks')" position="top">
              <span class="link-clicks-badge">({{ post.link_clicks }})</span>
            </Tooltip>
          </span>
          <span v-if="showShortLinks" class="cell-num">{{ fmtShortClicks(post) }}</span>
          <span class="cell-num">{{ post.reach_engagement ? post.reach_engagement.toFixed(2) + '%' : '—' }}</span>
          <Tooltip :text="scoreFormula" position="top"><span class="cell-stars">{{ starsFor(post.reach_engagement) }}</span></Tooltip>
          <a v-if="postLink(post)" :href="postLink(post)" target="_blank" rel="noopener" class="cell-link" @click.stop>
            <i class="material-icons">open_in_new</i>
          </a>
          <span v-else></span>
        </div>
      </div>

      <div class="pagination" v-if="pageCount > 1">
        <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">
          <i class="material-icons">chevron_left</i>
        </button>
        <span class="page-info">{{ currentPage }} / {{ pageCount }}</span>
        <button class="page-btn" :disabled="currentPage === pageCount" @click="currentPage++">
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
const PAGE_SIZE = 20

import Tooltip from '../tooltip/tooltipInit.vue'
import ReportingFilter from './ReportingFilter.vue'
import LabelPicker from '../posts/components/drawer/components/LabelPicker.vue'
import LabelsMixin from '@/components/mixins/Labels.vue'
import { parseLabelIcon } from '@/utils/labelIcon.js'
import vClickOutside from 'vue-click-outside'
import { shortLinksVisible, postShortLinkStats } from '@/utils/shortLinkMetrics'

// Icônes réactions Facebook (+ variantes connues de l'API)
const FB_REACTION_ICONS = {
  like:     require('@/assets/img/reporting_icons/like.svg'),
  love:     require('@/assets/img/reporting_icons/love.svg'),
  haha:     require('@/assets/img/reporting_icons/haha.svg'),
  wow:      require('@/assets/img/reporting_icons/wow.svg'),
  sad:      require('@/assets/img/reporting_icons/sad.svg'),
  anger:    require('@/assets/img/reporting_icons/angry.svg'),
  angry:    require('@/assets/img/reporting_icons/angry.svg'),  // alias API
  so:       require('@/assets/img/reporting_icons/sad.svg'),    // "sorry" — variante régionale
  sorry:    require('@/assets/img/reporting_icons/sad.svg'),
  care:     require('@/assets/img/reporting_icons/love.svg'),   // care ~ love
  thankful: require('@/assets/img/reporting_icons/love.svg'),
  pride:    require('@/assets/img/reporting_icons/like.svg'),
}
// Icônes réactions LinkedIn
const LI_REACTION_ICONS = {
  LIKE:          require('@/assets/img/reporting_icons/linkedin-like.png'),
  EMPATHY:       require('@/assets/img/reporting_icons/linkedin-love.png'),
  PRAISE:        require('@/assets/img/reporting_icons/linkedin-celebrate.png'),
  MAYBE:         require('@/assets/img/reporting_icons/linkedin-curious.png'),
  INTEREST:      require('@/assets/img/reporting_icons/linkedin-insightful.png'),
  ENTERTAINMENT: require('@/assets/img/reporting_icons/linkedin-support.png'),
}

export default {
  name: 'ReportingContent',
  components: { Tooltip, ReportingFilter, LabelPicker },
  directives: { clickOutside: vClickOutside },
  mixins: [LabelsMixin],
  props: {
    stats: { type: Object, required: true },
    page: { type: Object, default: null },
    solutionPostMap: { type: Object, default: function() { return {} } },
    // Clics des liens courts mws.li de la page (GET /reporting/short-links), null si indisponible
    shortLinks: { type: Object, default: null },
  },
  data() {
    return {
      sortKey: 'clicks',
      sortDir: 'desc',
      currentPage: 1,
      search: '',
      showFilter: false,
      labelPickerOpenFor: null,
      labelPickerPosition: { top: 0, left: 0 },
      labelPickerTriggerEl: null,
      labelPickerSnapshot: [],
      filter: {
        types: { image: true, video: true, story: true, carousel: true, link: true, document: true, poll: true, text: true },
        solutionOnly: false,
        creators: {},
        labels: [],
      },
    }
  },
  mounted() {
    // Always refresh (not just when empty) — otherwise labels created/edited
    // since the store was first populated this session never show up here.
    this.$store.dispatch('labels/init')
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateLabelPickerPosition, true)
    window.removeEventListener('resize', this.updateLabelPickerPosition)
  },
  computed: {
    network() {
      return this.page ? (this.page.network || '') : ''
    },
    // Tooltip propre au réseau (clé <base>_<network>), repli sur la clé générique
    networkTooltip() {
      var self = this
      return function(base, fallback) {
        var key = base + '_' + self.network
        return self.$te(key) ? self.$t(key) : self.$t(fallback || base)
      }
    },
    showShares() {
      // Instagram : colonne affichée dès que les partages ont pu être calculés
      return this.network !== 'instagram' || this.posts.some(function(p) { return typeof p.shares_count === 'number' })
    },
    showClicks() {
      return this.network === 'facebook' || this.network === 'linkedin'
    },
    // Colonne « Clics mws.li » : raccourcisseur actif et données sur la période
    showShortLinks() {
      return shortLinksVisible(this.shortLinks)
    },
    gridTemplate() {
      var reactW = (this.network === 'facebook' || this.network === 'linkedin') ? '130px' : '95px'
      var cols = ['155px', '1fr', '110px', reactW, '115px']
      if (this.showShares) cols.push('85px')
      if (this.showClicks) cols.push('100px')
      if (this.showShortLinks) cols.push('100px')
      cols.push('100px', '70px', '24px')
      return cols.join(' ')
    },
    scoreFormula() {
      return this.$t('dash.reporting.score_formula')
    },
    posts() {
      return this.stats.posts || (this.stats.page && this.stats.page.posts) || []
    },
    creatorsAvailable() {
      var vm = this
      var seen = {}
      this.posts.forEach(function(p) {
        var id = String(p.post_id || p.id || '')
        if (!id) return
        var entry = vm.solutionPostMap[id]
        if (entry && entry.creator && entry.creator.id && !seen[entry.creator.id]) {
          seen[entry.creator.id] = entry.creator
        }
      })
      return Object.values(seen)
    },
    labelsAvailable() {
      return this.$store.getters['labels/getLabelsList']
    },
    hasActiveFilters() {
      if (this.filter.solutionOnly) return true
      if (Object.values(this.filter.types).some(function(v) { return !v })) return true
      if (Object.values(this.filter.creators || {}).some(function(v) { return v === false })) return true
      if (this.filter.labels && this.filter.labels.length) return true
      return false
    },
    activeFilterCount() {
      var count = Object.values(this.filter.types).filter(function(v) { return !v }).length
      if (this.filter.solutionOnly) count++
      count += Object.values(this.filter.creators || {}).filter(function(v) { return v === false }).length
      count += (this.filter.labels && this.filter.labels.length) ? 1 : 0
      return count
    },
    filteredSortedPosts() {
      var vm = this
      var q = (this.search || '').toLowerCase().trim()
      var list = this.applyPostFilter(this.posts)
      if (q) {
        list = list.filter(function(p) {
          var msg = (p.message || p.caption || p.commentary || '').toLowerCase()
          return msg.includes(q)
        })
      }
      var dir = this.sortDir === 'asc' ? 1 : -1
      var key = this.sortKey
      list.sort(function(a, b) {
        var va, vb
        if (key === 'date')             { va = new Date(a.created_time); vb = new Date(b.created_time) }
        else if (key === 'impressions') { va = a.post_impressions || a.view_count || 0; vb = b.post_impressions || b.view_count || 0 }
        else if (key === 'reactions')   { va = a.post_reactions_sum || a.like_count || 0; vb = b.post_reactions_sum || b.like_count || 0 }
        else if (key === 'comments')    { va = a.comments_count || 0; vb = b.comments_count || 0 }
        else if (key === 'shares')      { va = a.shares_count || 0; vb = b.shares_count || 0 }
        else if (key === 'clicks')      { va = a.total_clicks || a.link_clicks || a.click_count || 0; vb = b.total_clicks || b.link_clicks || b.click_count || 0 }
        else if (key === 'short_clicks') { va = vm.shortClicks(a); vb = vm.shortClicks(b) }
        else if (key === 'engagement' || key === 'score') { va = a.reach_engagement || 0; vb = b.reach_engagement || 0 }
        else                            { va = new Date(a.created_time); vb = new Date(b.created_time) }
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
      return list
    },
    pageCount() {
      return Math.ceil(this.filteredSortedPosts.length / PAGE_SIZE)
    },
    paginatedFilteredPosts() {
      var start = (this.currentPage - 1) * PAGE_SIZE
      return this.filteredSortedPosts.slice(start, start + PAGE_SIZE)
    },
  },
  watch: {
    paginatedFilteredPosts: {
      immediate: true,
      handler(posts) {
        if (posts && posts.length) this._fetchPageImages(posts)
      },
    },
    search() {
      this.currentPage = 1
    },
    filter: {
      deep: true,
      handler() { this.currentPage = 1 },
    },
  },
  methods: {
    shortClicks(post) {
      var s = postShortLinkStats(this.shortLinks, this.solutionPostMap, post)
      return s ? s.clicks || 0 : 0
    },
    // Clics humains mws.li du post (— si aucun lien court rattaché)
    fmtShortClicks(post) {
      var s = postShortLinkStats(this.shortLinks, this.solutionPostMap, post)
      return s ? (s.clicks || 0) : '—'
    },
    closeFilter() {
      this.showFilter = false
    },
    normalizePostType(type) {
      if (!type) return 'text'
      var t = type.toLowerCase()
      if (t === 'photo' || t === 'image') return 'image'
      if (t === 'video' || t === 'reel' || t === 'reels' || t === 'clips' || t === 'live' || t === 'live_video') return 'video'
      if (t === 'story') return 'story'
      if (t === 'carrousel' || t === 'carousel' || t === 'carousel_album' || t === 'multi-image') return 'carousel'
      if (t === 'snippet' || t === 'link' || t === 'article') return 'link'
      if (t === 'document' || t === 'native_document') return 'document'
      if (t === 'poll' || t === 'sondage' || t === 'urn_reference') return 'poll'
      if (t === 'text' || t === 'text_only' || t === 'status' || t === 'none') return 'text'
      return 'text'
    },
    applyPostFilter(posts) {
      var vm = this
      var rf = this.filter
      var result = posts.slice()
      var allTypesOn = Object.values(rf.types).every(function(v) { return v })
      if (!allTypesOn) {
        result = result.filter(function(p) {
          return rf.types[vm.normalizePostType(p.type)] !== false
        })
      }
      if (rf.solutionOnly) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return false
          var entry = vm.solutionPostMap[id]
          if (!entry) return true
          return entry.is_solution_post
        })
      }
      if (rf.creators && Object.values(rf.creators).some(function(v) { return v === false })) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return true
          var entry = vm.solutionPostMap[id]
          var creatorId = entry && entry.creator && entry.creator.id
          if (!creatorId) return true
          return rf.creators[creatorId] !== false
        })
      }
      if (rf.labels && rf.labels.length) {
        result = result.filter(function(p) {
          var id = String(p.post_id || p.id || '')
          if (!id) return false
          var entry = vm.solutionPostMap[id]
          var postLabels = entry && entry.labels
          if (!postLabels || !postLabels.length) return false
          return rf.labels.some(function(labelId) { return postLabels.includes(labelId) })
        })
      }
      return result
    },
    async _fetchPageImages(posts) {
      if (!this.page) return
      var net = this.page.network || ''
      if (!['facebook', 'instagram', 'tiktok'].includes(net)) return
      var { VUE_APP_API_HOST } = process.env
      var token = localStorage.getItem('token')
      var vm = this
      var BATCH = 5
      var needFetch = posts.filter(function(post) {
        return !(post.attachment_url || post.full_picture || post.thumbnail_url || post.media_url || post.cover_image_url)
      })
      for (var i = 0; i < needFetch.length; i += BATCH) {
        await Promise.all(needFetch.slice(i, i + BATCH).map(async function(post) {
          var postId = post.post_id || post.id
          if (!postId) return
          try {
            if (net === 'tiktok') {
              if (post.cover_image_url) vm.$set(post, 'attachment_url', post.cover_image_url)
              return
            }
            var res = await fetch(VUE_APP_API_HOST + '/moderation/post?page=' + vm.page.id + '&post=' + postId + '&inbox=true', {
              headers: { Authorization: token ? 'Bearer ' + token : '' },
            })
            if (!res.ok) return
            var data = await res.json()
            if (!data || !data.post) return
            var imgUrl = net === 'facebook'
              ? data.post.full_picture
              : (data.post.thumbnail_url || data.post.media_url)
            if (imgUrl) vm.$set(post, 'attachment_url', imgUrl)
          } catch (e) { /* ignore */ }
        }))
      }
    },
    onSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = 'desc'
      }
      this.currentPage = 1
    },
    sortClass(key) {
      return { 'col-active': this.sortKey === key }
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'unfold_more'
      return this.sortDir === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
    },
    fmtImpression(post) {
      if (this.network === 'tiktok') return post.post_impressions || post.view_count || 0
      return post.post_impressions || 0
    },
    // Retourne les détails de réactions si disponibles, null sinon
    postReactionItems(post) {
      var rv = post.post_reactions && post.post_reactions[0] && post.post_reactions[0].value
      if (!rv) return null
      var icons = this.network === 'linkedin' ? LI_REACTION_ICONS : FB_REACTION_ICONS
      // Itérer sur TOUTES les clés présentes dans rv (y compris care, thankful, pride…)
      // pour que la somme corresponde exactement au total post_reactions[0].sum
      var items = Object.keys(rv)
        .filter(function(k) { return rv[k] > 0 })
        .map(function(k) { return { key: k, icon: icons[k] || null, label: k, count: rv[k] } })
      return items.length ? items : null
    },
    fmtTotalClicks(post) {
      return post.total_clicks || post.link_clicks || post.click_count || 0
    },
    fmtReactions(post) {
      // post_reactions[0].sum est la somme exacte de tous les types — priorité sur post_reactions_sum
      if (post.post_reactions && post.post_reactions[0] && post.post_reactions[0].sum != null) {
        return post.post_reactions[0].sum
      }
      if (this.network === 'tiktok') return post.like_count || 0
      return post.post_reactions_sum || post.like_count || 0
    },
    formatDate(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    postLink(post) {
      const url = post.permalink_url || post.share_url || post.permalink || null
      if (!url) return null
      return url.startsWith('http') ? url : 'https://' + url
    },
    starsFor(pct) {
      if (pct == null) return '—'
      var stars = 0
      if (pct >= 7) stars = 5
      else if (pct >= 4) stars = 4
      else if (pct >= 2) stars = 3
      else if (pct >= 1) stars = 2
      else if (pct >= 0.5) stars = 1
      return '★'.repeat(stars) + '☆'.repeat(5 - stars)
    },
    typeLabel(type) {
      var map = {
        photo: 'Image', image: 'Image', IMAGE: 'Image',
        video: 'Vidéo', VIDEO: 'Vidéo',
        reel: 'Reel', REELS: 'Reel', clips: 'Reel',
        live: 'Live', live_video: 'Live',
        snippet: 'Lien', link: 'Lien', ARTICLE: 'Lien', article: 'Lien',
        text: 'Texte', text_only: 'Texte', status: 'Texte', NONE: 'Texte',
        Story: 'Story', story: 'Story',
        carrousel: 'Carrousel', carousel: 'Carrousel', CAROUSEL_ALBUM: 'Carrousel', 'MULTI-IMAGE': 'Carrousel',
        document: 'Document', NATIVE_DOCUMENT: 'Document',
        poll: 'Sondage', sondage: 'Sondage', URN_REFERENCE: 'Sondage',
      }
      return map[type] || type || '—'
    },
    cap(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    isSolutionPost(post) {
      var id = String(post.post_id || post.id || '')
      if (!id) return false
      var entry = this.solutionPostMap[id]
      return !!(entry && entry.is_solution_post)
    },
    postKey(post) {
      return String(post.post_id || post.id || '')
    },
    newsIdFor(post) {
      var entry = this.solutionPostMap[this.postKey(post)]
      return (entry && entry.news_id) || null
    },
    postLabelIds(post) {
      var entry = this.solutionPostMap[this.postKey(post)]
      return (entry && entry.labels) || []
    },
    resolvedLabelsFor(post) {
      var allLabels = this.$store.getters['labels/getLabels']
      return this.postLabelIds(post).map(function(id) { return allLabels[id] }).filter(Boolean)
    },
    parseIcon(icon) {
      return parseLabelIcon(icon)
    },
    toggleLabelPicker(event, post) {
      var key = this.postKey(post)
      if (this.labelPickerOpenFor === key) {
        this.closeLabelPicker(post)
        return
      }
      // position:fixed (so the popover escapes the .post-table scroll container's
      // overflow clipping) means it must be re-anchored to the trigger button on
      // every scroll/resize while open, otherwise it stays put in the viewport
      // instead of following the row.
      this.labelPickerTriggerEl = event.currentTarget
      this.updateLabelPickerPosition()
      this.labelPickerOpenFor = key
      this.labelPickerSnapshot = this.postLabelIds(post).slice()
      window.addEventListener('scroll', this.updateLabelPickerPosition, true)
      window.addEventListener('resize', this.updateLabelPickerPosition)
    },
    updateLabelPickerPosition() {
      if (!this.labelPickerTriggerEl) return
      var rect = this.labelPickerTriggerEl.getBoundingClientRect()
      this.labelPickerPosition = { top: rect.bottom + 4, left: rect.left }
    },
    // Only updates the local chip display while the picker is open — the actual
    // save happens once on close, in closeLabelPicker(), so selecting several
    // labels in a row doesn't fire a request per click.
    onLabelsUpdate(post, newLabelIds) {
      var entry = this.solutionPostMap[this.postKey(post)]
      if (entry) this.$set(entry, 'labels', newLabelIds)
    },
    closeLabelPicker(post) {
      // Guards against a double-fire on the same click: clicking the backdrop
      // triggers both its own @click and LabelPicker's internal click-outside
      // (both reach this method) — the second call must be a no-op.
      if (!this.labelPickerOpenFor) return
      this.labelPickerOpenFor = null
      this.labelPickerTriggerEl = null
      window.removeEventListener('scroll', this.updateLabelPickerPosition, true)
      window.removeEventListener('resize', this.updateLabelPickerPosition)
      var newsId = post && this.newsIdFor(post)
      if (!newsId) return
      var before = JSON.stringify(this.labelPickerSnapshot.slice().sort())
      var after = JSON.stringify(this.postLabelIds(post).slice().sort())
      if (before !== after) this.persistLabels(post, newsId)
    },
    async persistLabels(post, newsId) {
      var labels = this.postLabelIds(post)
      await this.setPostLabels({ id: newsId, labels: labels })
      var entry = this.solutionPostMap[this.postKey(post)]
      if (entry) this.$set(entry, 'labels', labels)
    },
    typeBadgeClass(type) {
      if (!type) return 'badge-default'
      var t = type.toLowerCase()
      if (t === 'photo' || t === 'image') return 'badge-image'
      if (t === 'video') return 'badge-video'
      if (t === 'reel' || t === 'reels' || t === 'clips') return 'badge-reel'
      if (t === 'live' || t === 'live_video') return 'badge-live'
      if (t === 'snippet' || t === 'link' || t === 'article') return 'badge-link'
      if (t === 'story') return 'badge-story'
      if (t === 'carrousel' || t === 'carousel' || t === 'multi-image' || t === 'carousel_album') return 'badge-carousel'
      if (t === 'document' || t === 'native_document') return 'badge-document'
      if (t === 'poll' || t === 'sondage' || t === 'urn_reference') return 'badge-poll'
      return 'badge-default'
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; flex: 1; }

.filter-wrap {
  position: relative;
  flex-shrink: 0;
}
.filter-toggle-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea !important;
  background: #fff !important;
  color: #6B1F4D !important;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
}
.filter-toggle-btn:hover,
.filter-toggle-btn--active {
  background: #f1edf5 !important;
  border-color: #6B1F4D !important;
}
.filter-toggle-btn .material-icons { font-size: 16px; }
.filter-badge {
  background: #6B1F4D;
  color: #fff;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  min-width: 16px;
  text-align: center;
  line-height: 1.4;
}

.posts-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding: 7px 12px;
  background: #faf7fc;
  border: 1px solid #ede6f3;
  border-radius: 8px;
}
.posts-search__icon { font-size: 18px; color: #b08aa0; flex-shrink: 0; }
.posts-search__input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #121826;
  outline: none;
}
.posts-search__input::placeholder { color: #b08aa0; }
.posts-search__clear {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #b08aa0;
  display: flex;
  align-items: center;
  &:hover { color: #6b1f4d; }
  .material-icons { font-size: 16px; }
}

.cell-clicks { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
.link-clicks-badge {
  font-size: 11px;
  color: #9ba3b7;
  cursor: help;
}

.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }

.post-table { width: 100%; overflow-x: auto; }
.post-table__head,
.post-table__row {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 8px 4px;
}
.post-table__head {
  border-bottom: 1px solid #f1edf5;
  padding-bottom: 10px;
}
.post-table__row {
  border-bottom: 1px solid #f9f7fb;
  font-size: 13px;
  color: #121826;
}
.post-table__row:last-child { border-bottom: none; }
.align-right { text-align: right; justify-content: flex-end; }

.col-header {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
}
.col-header.sortable {
  cursor: pointer;
  user-select: none;
}
.col-header.sortable:hover { color: #6B1F4D; }
.col-header.col-active { color: #6B1F4D; }
.sort-icon { font-size: 14px; opacity: 0.6; }
.col-active .sort-icon { opacity: 1; }

.cell-date { color: #9ba3b7; font-size: 12px; white-space: nowrap; min-width: 0; }
.cell-post { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
.post-text { display: flex; flex-direction: column; gap: 4px; min-width: 0; align-items: flex-start; }
.post-thumb {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 5.6px;
  overflow: hidden;
  background: #f1edf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0a8d0;
}
.post-thumb img { width: 100%; height: 100%; object-fit: cover; }
.post-thumb .material-icons { font-size: 18px; }
.post-msg {
  font-size: 12px;
  color: #555E75;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}
.cell-num { text-align: right; font-size: 13px; font-weight: 500; min-width: 0; }

.cell-reactions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 0;
}
.reaction-grid {
  display: flex;
  gap: 5px;
  justify-content: flex-end;
  flex-wrap: nowrap;
}
.reaction-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  /* chaque colonne s'adapte à sa valeur la plus large */
}
.reaction-icon { width: 14px; height: 14px; flex-shrink: 0; }
.reaction-unknown {
  font-size: 8px;
  font-weight: 700;
  color: #888;
  background: #f0ecf5;
  border-radius: 3px;
  padding: 1px 2px;
  line-height: 14px;
  text-align: center;
}
.reaction-count {
  font-size: 10px;
  color: #555E75;
  font-weight: 500;
  white-space: nowrap;   /* la valeur ne coupe pas */
  text-align: center;
  line-height: 1;
}
.reaction-total {
  font-size: 12px;
  font-weight: 700;
  color: #121826;
  border-top: 1px solid #f1edf5;
  padding-top: 3px;
  margin-top: 1px;
  text-align: right;
  width: 100%;
}
.cell-stars {
  text-align: right;
  font-size: 12px;
  color: #f59e0b;
  letter-spacing: 1px;
  cursor: help;
}
.score-header { gap: 4px; }
.cell-link {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ba3b7;
  transition: color 0.15s;
}
.cell-link:hover { color: #6B1F4D; }
.cell-link .material-icons { font-size: 16px; }
.score-info { font-size: 11px; color: #9ba3b7; cursor: help; }

.post-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
}
.post-meta .type-badge { margin-top: 0; }

.post-label-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #fff;
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 10.5px;
  font-weight: 600;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.post-label-chip i { font-size: 9px; }
.post-label-emoji { font-size: 11px; line-height: 1; }
.post-label-add {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  cursor: pointer;
  border: 1px solid #e8dae5;
  transition: background 0.15s;
}
.post-label-add i { font-size: 9px; }
/* Custom tooltip: absolutely positioned (out of the flex flow), so it never
   changes this button's own width/wrap behavior on hover — unlike an inline
   expanding label, which can push the button across a flex-wrap boundary and
   make it flicker in and out of :hover. */
.post-label-add span {
  position: absolute;
  bottom: calc(100% + 7px);
  left: 50%;
  transform: translateX(-50%);
  background: #121826;
  color: #fff;
  font-size: 10.5px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 5px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 1001;
}
.post-label-add span::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #121826;
}
.post-label-add:hover {
  background: #e8dae5;
}
.post-label-add:hover span {
  opacity: 1;
}
.post-label-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}
.post-label-picker-popover {
  position: fixed;
  z-index: 1000;
}
.type-badge {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  max-width: fit-content;
}
.badge-image { background: #dcfce7; color: #166534; }
.badge-video { background: #dbeafe; color: #1e40af; }
.badge-reel { background: #ede9fe; color: #5b21b6; }
.badge-live { background: #fee2e2; color: #991b1b; }
.badge-link { background: #ffedd5; color: #9a3412; }
.badge-story { background: #f3e8ff; color: #6b21a8; }
.badge-carousel { background: #cffafe; color: #164e63; }
.badge-document { background: #fef9c3; color: #854d0e; }
.badge-poll { background: #fce7f3; color: #9d174d; }
.badge-default { background: #f1f5f9; color: #475569; }

.solution-badge {
  display: inline-flex;
  align-items: center;
  cursor: help;
  vertical-align: middle;
}
.solution-badge__icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: block;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f1edf5;
}
.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  cursor: pointer;
  transition: all 0.15s;
}
.page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.page-btn:not(:disabled):hover { background: #f1edf5; }
.page-btn .material-icons { font-size: 18px; }
.page-info { font-size: 13px; color: #555E75; font-weight: 500; min-width: 60px; text-align: center; }
</style>
